'use client';

import Link from 'next/link';
import { type CSSProperties, useEffect, useState } from 'react';
import { ArrowLeft, ChevronUp, Home } from 'lucide-react';

type GuideBackRailProps = {
  href: string;
  homeHref: string;
  variant?: 'default' | 'v2';
};

type GuideBackRailState = {
  left: number;
  offset: number;
  top: number;
};

export default function GuideBackRail({
  href,
  homeHref,
  variant = 'default',
}: GuideBackRailProps) {
  const [state, setState] = useState<GuideBackRailState | null>(null);

  useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      const card = document.querySelector('.guide-detail-card');
      if (!card) return;

      const cardRect = card.getBoundingClientRect();
      const reference =
        document.querySelector('.guide-detail-toc') ||
        document.querySelector('.guide-detail-sidebar') ||
        card;
      const referenceRect = reference.getBoundingClientRect();
      const nextState = {
        left: Math.round(cardRect.left),
        offset: Math.round(
          Math.max(variant === 'v2' ? -60 : -48, 8 - cardRect.left),
        ),
        top: Math.max(20, Math.round(referenceRect.top)),
      };

      setState((current) => {
        if (
          current &&
          current.left === nextState.left &&
          current.offset === nextState.offset &&
          current.top === nextState.top
        ) {
          return current;
        }
        return nextState;
      });
    };

    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    const card = document.querySelector('.guide-detail-card');
    const layout = document.querySelector('.guide-detail-layout');
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleUpdate)
        : null;
    if (card) resizeObserver?.observe(card);
    if (layout) resizeObserver?.observe(layout);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [variant]);

  useEffect(() => {
    const footer = document.querySelector('.shared-game-footer');
    if (!footer) return;

    const root = document.documentElement;
    const observer = new IntersectionObserver(([entry]) => {
      root.classList.toggle('guide-footer-visible', entry.isIntersecting);
    });
    observer.observe(footer);

    return () => {
      observer.disconnect();
      root.classList.remove('guide-footer-visible');
    };
  }, []);

  const style = (
    state
      ? {
          '--guide-back-left': `${state.left}px`,
          '--guide-back-offset': `${state.offset}px`,
          '--guide-back-top': `${state.top}px`,
        }
      : undefined
  ) as CSSProperties | undefined;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav
      className={`guide-detail-back-rail is-stuck${variant === 'v2' ? ' guide-detail-back-rail--v2' : ''}`}
      aria-label="返回攻略"
      style={style}
    >
      <Link className="guide-back-link" href={href}>
        <ArrowLeft aria-hidden="true" size={16} />
        <span>返回攻略</span>
      </Link>
      <div className="guide-rail-links" aria-label="页面导航">
        {variant === 'v2' ? (
          <>
            <Link className="guide-rail-link" href={homeHref} aria-label="首页">
              <Home aria-hidden="true" size={18} />
            </Link>
            <Link className="guide-rail-link guide-rail-brand" href="/" aria-label="GAME WIKI">
              G
            </Link>
          </>
        ) : (
          <>
            <Link className="guide-rail-link guide-rail-brand" href="/" aria-label="GAME WIKI">
              G
            </Link>
            <Link className="guide-rail-link" href={homeHref} aria-label="首页">
              <Home aria-hidden="true" size={18} />
            </Link>
          </>
        )}
      </div>
      {variant === 'v2' && (
        <button
          className="guide-rail-scroll-top"
          type="button"
          aria-label="回到顶部"
          onClick={scrollToTop}
        >
          <ChevronUp aria-hidden="true" size={18} />
        </button>
      )}
    </nav>
  );
}
